import * as Select from "@radix-ui/react-select";
import { CaretDownIcon } from "@phosphor-icons/react";
import { PhosphorIcon } from "../phosphorIcon/PhosphorIcon";

const TRIGGER_CLASSES =
    "min-h-[40px] rounded-pill border border-p6-grey-mid bg-white font-primary text-m text-p6-grey-dark px-s py-xxs flex items-center justify-between gap-xs outline-none w-full hover:border-p6-orange cursor-pointer";
// z-index must beat BasePopupWrapper's overlay (z-index 1000) so the dropdown stays visible inside popups.
const CONTENT_CLASSES =
    "rounded-[12px] bg-white border border-p6-grey-lightest shadow-[0_2px_8px_rgba(0,0,0,0.12)] overflow-hidden z-[1100]";
// First/last items round to match the container so a colored item background doesn't leave a
// square-cornered gap under the rounded frame (radius minus the 1px border).
const ITEM_CLASSES =
    "font-primary text-m text-p6-grey-dark min-h-[40px] leading-[1.5] py-xs px-s cursor-pointer outline-none data-[highlighted]:bg-p6-orange/25 data-[state=checked]:bg-p6-orange data-[state=checked]:text-white first:rounded-t-[11px] last:rounded-b-[11px] data-[disabled]:text-p6-grey-mid data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed";

interface SortSelectProps {
    options: Array<{ name: string; value: string; disabled?: boolean }>;
    value: string;
    onChange: (value: string) => void;
    "aria-labelledby"?: string;
}

const SortSelect = (props: SortSelectProps) => {
    return (
        <Select.Root
            value={props.value}
            onValueChange={props.onChange}
        >
            <Select.Trigger
                className={TRIGGER_CLASSES}
                aria-labelledby={props["aria-labelledby"]}
            >
                {/* All options are rendered invisibly in the same grid cell so the trigger
                    always sizes to the widest option, preventing width jumps on selection change.
                    min-w-no on the cell and its children lets the trigger shrink below the widest
                    option on narrow screens; the value then wraps and the trigger grows with it. */}
                <span className="grid [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:min-w-no min-w-no overflow-hidden text-left">
                    <Select.Value className="break-words" />
                    {props.options.map((opt) => (
                        <span
                            key={opt.value}
                            className="invisible whitespace-nowrap"
                        >
                            {opt.name}
                        </span>
                    ))}
                </span>
                <Select.Icon className="flex items-center shrink-0">
                    <PhosphorIcon
                        phosphorIcon={CaretDownIcon}
                        color="text-p6-grey-dark"
                        fontSize="text-m"
                    />
                </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
                <Select.Content
                    position="popper"
                    sideOffset={8}
                    className={CONTENT_CLASSES}
                    style={{ minWidth: "var(--radix-select-trigger-width)" }}
                >
                    <Select.Viewport>
                        {props.options.map((opt) => (
                            <Select.Item
                                key={opt.value}
                                value={opt.value}
                                disabled={opt.disabled}
                                className={ITEM_CLASSES}
                            >
                                <Select.ItemText>{opt.name}</Select.ItemText>
                            </Select.Item>
                        ))}
                    </Select.Viewport>
                </Select.Content>
            </Select.Portal>
        </Select.Root>
    );
};

export default SortSelect;
